import { ChangeEvent, useMemo } from 'react';
import styled from '@emotion/styled';

// colors
import { primary, gray, red, white } from '@styles/Colors';

// constants
import { TextInputAlign, TextInputSize, TextInputVariant, TypoVariant } from '@constants/atoms';
import { MOBILE_MODE } from '@constants/size';
import { Radius } from '@styles/Radius';
import { useIsMobile } from '@hooks/responsive/useIsMobile';

import styles from './TextInput.module.css';
import { Tooltip } from 'react-tooltip';
import Typography from '../Typography';

type Props = Styleable & {
  variant?: TextInputVariant;
  placeholder?: string;
  name?: string;
  value?: string | number;
  size?: TextInputSize;
  type?: string;
  align?: TextInputAlign;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  tooltipMessage?: string;
  autoComplete?: string;
};

type CssProps = {
  variant: TextInputVariant;
  textAlign: string;
  borderColor: string;
  color: string;
  placeholderColor: string;
  background: string;
};

const TextInput = styled.input<CssProps>`
  /**TypoSzie.B4 */
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.28px;
  font-weight: 400;

  height: max-content;
  border-radius: ${Radius.MEDIUM};
  border: 1px solid ${({ borderColor }) => borderColor};
  background: ${({ background }) => background};
  color: ${({ color }) => color};
  text-align: ${({ textAlign }) => textAlign};

  &:focus {
    border-color: ${({ variant }) => variant === TextInputVariant.DEFAULT && primary.gray};
    outline: none;
  }
  &::placeholder {
    color: ${({ placeholderColor }) => placeholderColor};
  }

  @media ${MOBILE_MODE} {
    border-radius: ${Radius.SMALL};
    height: 40px;
    font-size: 14px;
  }
`;

export default function TextInputComponent({
  variant = TextInputVariant.DEFAULT,
  style,
  type = 'text',
  placeholder,
  name,
  size,
  align = TextInputAlign.LEFT,
  onChange,
  onKeyDown,
  value,
  tooltipMessage,
  onFocus,
  onBlur = onChange,
  autoComplete,
}: Props) {
  const TRANSPARENT = 'transparent';
  const isMobile = useIsMobile();

  const [borderColor, color, placeholderColor, background] = useMemo(() => {
    switch (variant) {
      case TextInputVariant.DEFAULT:
        return [gray.gray4, primary.gray, gray.gray4, TRANSPARENT];
      case TextInputVariant.ERROR:
        return [red.red3, primary.gray, gray.gray4, TRANSPARENT];
      case TextInputVariant.FIXED:
        return [gray.gray5, gray.gray5, gray.gray5, gray.gray1];
    }
  }, [variant]);

  const textInputSize = useMemo(() => {
    if (size) return size;

    return isMobile ? TextInputSize.MEDIUM : TextInputSize.LARGE;
  }, [isMobile, size]);

  const padding = useMemo(() => {
    switch (textInputSize) {
      case TextInputSize.LARGE:
        return '14px 12px 14px 16px';
      case TextInputSize.MEDIUM:
        return '10px 12px 10px 16px';
      case TextInputSize.SMALL:
        return '8px 12px 8px 16px';
      case TextInputSize.XSMALL:
        return '8px 12px 4px 12px';
    }
  }, [textInputSize]);

  return (
    <>
      <TextInput
        style={{ padding, ...style }}
        textAlign={align}
        variant={variant}
        borderColor={borderColor}
        color={color}
        placeholderColor={placeholderColor}
        background={background}
        placeholder={placeholder}
        name={name}
        value={value}
        type={type}
        disabled={variant === TextInputVariant.FIXED}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        autoComplete={autoComplete}
        data-tooltip-id={tooltipMessage ? 'tooltip' : ''}
        data-tooltip-place={'top-start'}
      />
      <Tooltip id="tooltip" className={styles.tooltip}>
        <Typography variant={TypoVariant.MH4} color={white}>
          {tooltipMessage}
        </Typography>
      </Tooltip>
    </>
  );
}
